import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FormField } from "@/components/ui/form"
import { TranslatedFieldError } from "@/components/ui/form-field-error"
import { Input, TextArea } from "@/components/ui/input"
import {
  LabelAndSelectValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { useToast } from "@/hooks/useToast"
import { MultipleSelect } from "@/modules/patients/components/multiple-select"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import {
  DOCUMENT_LANGUAGES,
  PREGNANT,
  REQUEST_EXAMINATION_GROUP,
  RequestExaminationOption,
} from "@/modules/patients/detail/meas-radiology-prescription/ch/constants"
import {
  CLINICAL_ASSESSMENT_MAX_LENGTH,
  MeasRadiologyPrescription,
  MeasRadiologyPrescriptionSchema,
  QUESTION_MAX_LENGTH,
} from "@/modules/patients/detail/meas-radiology-prescription/ch/schema/meas-radiology-prescription-schema"
import { chMeasRadiologyPrescriptionFragment$key } from "@/modules/patients/detail/meas-radiology-prescription/ch/services/__generated__/chMeasRadiologyPrescriptionFragment.graphql"
import { chMeasRadiologyPrescriptionFragment } from "@/modules/patients/detail/meas-radiology-prescription/ch/services/chMeasRadiologyPrescriptionFragment"
import { useChCreateRadiologyPrescriptionMutation } from "@/modules/patients/detail/meas-radiology-prescription/ch/services/useChCreateRadiologyPrescriptionMutation"
import { useChDeleteRadiologyPrescriptionMutation } from "@/modules/patients/detail/meas-radiology-prescription/ch/services/useChDeleteRadiologyPrescriptionMutation"
import { useChGenerateRadiologyPrescriptionDocumentMutation } from "@/modules/patients/detail/meas-radiology-prescription/ch/services/useChGenerateRadiologyPrescriptionDocumentMutation"
import { useChUpdateRadiologyPrescriptionMutation } from "@/modules/patients/detail/meas-radiology-prescription/ch/services/useChUpdateRadiologyPrescriptionMutation"
import { mapValuesToOptions } from "@/modules/patients/detail/meas-radiology-prescription/utils/map-values-to-options"
import { getDocumentLanguageLabel } from "@/modules/patients/hooks/useDefaultDocumentLanguage"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { getUploadSessionCode } from "@/modules/patients/libs/get-upload-session-code"
import { chProgressNoteFragment$data } from "@/modules/patients/services/ch/__generated__/chProgressNoteFragment.graphql"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface ChMeasRadiologyPrescriptionDrawerProps {
  progressNoteId: string
  id?: string
  radiologyPrescriptionRef?: chMeasRadiologyPrescriptionFragment$key
  fileUploadRequest?: NonNullable<chProgressNoteFragment$data["followUpAppointment"]>["fileUploadRequest"]
  medgateConnectOrder?: NonNullable<chProgressNoteFragment$data["followUpAppointment"]>["medgateConnectOrder"]
}

const ChMeasRadiologyPrescriptionDrawer = withErrorBoundary(
  ({
    radiologyPrescriptionRef,
    progressNoteId,
    id,
    fileUploadRequest,
    medgateConnectOrder,
  }: ChMeasRadiologyPrescriptionDrawerProps) => {
    const { t } = useTranslation()
    const { toast } = useToast()
    const closeDrawer = useDrawerStore((state) => state.closeDrawer)
    const [createRadiologyPrescription, isCreating] = useChCreateRadiologyPrescriptionMutation()
    const [updateRadiologyPrescription, isUpdating] = useChUpdateRadiologyPrescriptionMutation()
    const [deleteRadiologyPrescription] = useChDeleteRadiologyPrescriptionMutation()
    const [generateRadiologyPrescription, isGenerating] = useChGenerateRadiologyPrescriptionDocumentMutation()
    const patientId = usePatientParam()
    const isEditMode = !!id
    const isAddMode = !isEditMode && patientId && progressNoteId
    const persistedData = useFragment(chMeasRadiologyPrescriptionFragment, radiologyPrescriptionRef)
    const updatedAt = persistedData?.updatedAt ?? persistedData?.createdAt
    const updatedBy = persistedData?.updatedBy ?? persistedData?.createdBy

    const confirm = useConfirmStore((state) => state.confirm)
    const cancel = useConfirmStore((state) => state.cancel)

    const parsedData = isEditMode
      ? MeasRadiologyPrescriptionSchema.parse({
          ...persistedData,
        })
      : null

    const form = useForm<MeasRadiologyPrescription>({
      mode: "all",
      resolver: zodResolver(MeasRadiologyPrescriptionSchema),
      defaultValues:
        isEditMode && parsedData
          ? {
              ...parsedData,
            }
          : {
              language: "GERMAN",
              requestExaminations: [],
            },
    })

    const {
      control,
      formState: { isDirty, errors },
      register,
    } = form

    useDirtyDrawerMaker(isDirty)

    const handleCloseDrawer = () => {
      closeDrawer()
      FaroInsightsWrapper.pushEvent("documents_meas_radio_close")
    }

    const onSubmit: SubmitHandler<MeasRadiologyPrescription> = (data) => {
      if (!progressNoteId) return

      if (isAddMode) {
        createRadiologyPrescription({
          variables: {
            input: {
              patientId,
              progressNoteId,
              language: data.language,
              requestExaminations: data.requestExaminations,
              clinicalAssessment: data.clinicalAssessment,
              question: data.question,
              pregnantStatus: data.pregnantStatus,
              employer: data.employer,
            },
          },
          onCompleted(response) {
            const id = response.ch_createMeasRadiologyPrescription.ch_MeasRadiologyPrescription?.id
            if (id) {
              handleGenerateRadiologyPrescription(id, data)
            }
          },
          updater(store, data) {
            try {
              const progressNoteRecordProxy = store.get(progressNoteId)
              const radiologyPrescription = data?.ch_createMeasRadiologyPrescription?.ch_MeasRadiologyPrescription
              if (radiologyPrescription?.id) {
                const newRecordProxy = store.get(radiologyPrescription.id)
                if (newRecordProxy) {
                  progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "ch_measRadiologyPrescription")
                }
              }
            } catch (error) {
              console.error("Error while processing data:", error)
            }
          },
        })
      }

      if (isEditMode) {
        updateRadiologyPrescription({
          variables: {
            input: {
              id,
              language: data.language,
              requestExaminations: data.requestExaminations,
              clinicalAssessment: data.clinicalAssessment,
              question: data.question,
              pregnantStatus: data.pregnantStatus,
              employer: data.employer,
            },
          },
          onCompleted({ ch_updateMeasRadiologyPrescription }) {
            const id = ch_updateMeasRadiologyPrescription.ch_MeasRadiologyPrescription?.id
            if (id) {
              handleGenerateRadiologyPrescription(id, data)
            }
          },
        })
      }
    }

    const onDelete = () => {
      if (progressNoteId && id) {
        deleteRadiologyPrescription({
          variables: {
            input: {
              id,
            },
          },
          onCompleted() {
            handleCloseDrawer()
            toast({
              description: t("patient.measRadiologyPrescription.deleteMessage.success"),
            })
            cancel()
          },
        })
      }
    }

    const handleDelete = () => {
      confirm({
        title: t("patient.measRadiologyPrescription.deleteConfirmTitle"),
        content: t("episodeOfCare.deleteProgressNote.content"),
        onConfirm: onDelete,
        confirmVariant: "danger",
        confirmText: t("common.button.delete"),
        cancelText: t("common.button.keep"),
      })
    }

    const handleGenerateRadiologyPrescription = (id: string, data: MeasRadiologyPrescription) => {
      if (id) {
        generateRadiologyPrescription({
          id,
          requestExaminations: data.requestExaminations,
          uploadSessionCode: getUploadSessionCode(fileUploadRequest, medgateConnectOrder),
          onCompleted() {
            handleCloseDrawer()
          },
        })
      }
    }

    return (
      <Drawer open onOpenChange={handleCloseDrawer}>
        <FormProvider {...form}>
          <DrawerContent>
            <DrawerClose className="top-2 right-2" />
            <DrawerHeader className="pb-4" data-testid="meas-radiology-prescription-header">
              <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
                {t("patient.measRadiologyPrescription.radiologyPrescriptionMEAS")}
              </DrawerTitle>
            </DrawerHeader>
            <DrawerBody data-testid="meas-radiology-prescription-body">
              <div className="mb-8 flex flex-col gap-4">
                <FormField
                  control={control}
                  name="language"
                  render={({
                    field: { name, onChange, value, ref },
                    formState: { errors },
                    fieldState: { invalid },
                  }) => (
                    <div>
                      <Select value={value} onValueChange={onChange} {...ref}>
                        <SelectTrigger hasError={invalid} className="w-56.5">
                          <SelectValue asChild>
                            <LabelAndSelectValue
                              data-testid="document-language"
                              label={t("patient.measRadiologyPrescription.documentLanguage") + "*"}
                              value={t(getDocumentLanguageLabel(value))}
                            />
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {DOCUMENT_LANGUAGES.map((language) => (
                            <SelectItem
                              value={language.value}
                              key={language.value}
                              data-testid={`document-language-${language.value.toLocaleLowerCase()}`}>
                              {t(language.label)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <TranslatedFieldError errors={errors} name={name} />
                    </div>
                  )}
                />
              </div>
              <div className="flex flex-col gap-4">
                <label className="text-on-surface text-base font-medium">
                  {t("patient.measRadiologyPrescription.for")}
                </label>
                <div className="space-y-4">
                  <FormField
                    name="requestExaminations"
                    control={control}
                    render={({ field: { value, onChange }, fieldState: { error } }) => {
                      const allOptions = REQUEST_EXAMINATION_GROUP.flatMap((group) => group.options)
                      const options = mapValuesToOptions(value, allOptions)

                      return (
                        <MultipleSelect<RequestExaminationOption>
                          data-testid="requested-examination"
                          value={options}
                          groups={REQUEST_EXAMINATION_GROUP}
                          getOptionLabel={(option) => option.name}
                          getOptionValue={(option) => option.value}
                          translateError
                          required
                          label={t("patient.measRadiologyPrescription.requestedExamination")}
                          error={error?.message}
                          onChange={(options) => {
                            onChange(options.map((item) => item.value))
                          }}
                        />
                      )
                    }}
                  />
                  <TextArea
                    {...register("clinicalAssessment")}
                    label={t("patient.measRadiologyPrescription.clinicalAssessment")}
                    data-testid="clinical-assessment-field"
                    error={errors.clinicalAssessment?.message}
                    required
                    translateError
                    textLimit={CLINICAL_ASSESSMENT_MAX_LENGTH}
                  />
                  <TextArea
                    {...register("question")}
                    label={t("patient.measRadiologyPrescription.question")}
                    data-testid="question-field"
                    error={errors.question?.message}
                    required
                    translateError
                    textLimit={QUESTION_MAX_LENGTH}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-4 pt-8">
                <label className="text-on-surface text-base font-medium">
                  {t("patient.measRadiologyPrescription.medicalInformation")}
                </label>
                <div className="space-y-4">
                  <FormField
                    control={control}
                    name="pregnantStatus"
                    render={({ field: { name, onChange, value, ref }, fieldState: { error } }) => {
                      const label = t("patient.measRadiologyPrescription.currentlyPregnant") + "*"
                      const getLabel = (value: string) => t(`patient.measRadiologyPrescription.pregnant.${value}`)
                      return (
                        <div data-testid="pregnant-status">
                          <Select value={value} onValueChange={onChange} {...ref}>
                            <SelectTrigger hasError={!!error}>
                              <SelectValue asChild placeholder={label}>
                                <LabelAndSelectValue
                                  data-testid="pregnant-select"
                                  label={label}
                                  value={getLabel(value)}
                                />
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {PREGNANT.map((option) => (
                                <SelectItem
                                  value={option}
                                  key={option}
                                  data-testid={`select-option-${option.toLowerCase()}`}>
                                  {getLabel(option)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <TranslatedFieldError errors={errors} name={name} />
                        </div>
                      )
                    }}
                  />
                  <Input
                    {...register("employer")}
                    label={t("patient.measRadiologyPrescription.employer")}
                    required
                    translateError
                    error={errors.employer?.message}
                  />
                </div>
              </div>
            </DrawerBody>
            <DrawerFooter className="flex-wrap gap-2" data-testid="meas-radiology-prescription-footer">
              {isEditMode && (
                <div className="flex gap-2">
                  {updatedAt && updatedBy && <LastModificationInfo updatedAt={updatedAt} updatedBy={updatedBy} />}
                  <button
                    onClick={() => {
                      FaroInsightsWrapper.pushEvent("documents_meas_radio_delete")
                      handleDelete()
                    }}
                    data-testid="meas-radiology-prescription-delete-button">
                    <TrashSimple className="text-on-surface-subdued" />
                  </button>
                </div>
              )}
              <div className="ml-auto flex gap-2">
                <Button
                  data-testid="meas-radiology-prescription-cancel-button"
                  variant="secondary"
                  onClick={handleCloseDrawer}>
                  {t("common.button.cancel")}
                </Button>
                <Button
                  disabled={isEditMode && !isDirty}
                  data-testid="meas-radiology-prescription-save-button"
                  isLoading={isCreating || isUpdating || isGenerating}
                  onClick={() => {
                    FaroInsightsWrapper.pushEvent("documents_meas_radio_save")
                    form.handleSubmit(onSubmit)()
                  }}>
                  {t("common.button.save")}
                </Button>
              </div>
            </DrawerFooter>
          </DrawerContent>
        </FormProvider>
      </Drawer>
    )
  },
  "ChMeasRadiologyPrescriptionDrawer",
)

export { ChMeasRadiologyPrescriptionDrawer }
